import React, { useCallback, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";

const DAY_MS = 24 * 60 * 60 * 1000;

const SliderContainer = styled.div`
  margin: 8px 0 4px;
`;

const SliderLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RangeWrapper = styled.div`
  position: relative;
  height: 24px;
  margin: 0 8px;
`;

const Track = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.borderColor};
`;

const ActiveTrack = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.green};
`;

const RangeInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  appearance: none;
  background: transparent;
  pointer-events: none;

  &::-webkit-slider-runnable-track {
    height: 4px;
    background: transparent;
  }

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.green};
    border: 2px solid ${({ theme }) => theme.black};
    cursor: pointer;
    pointer-events: auto;
    margin-top: -6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  &::-moz-range-track {
    height: 4px;
    background: transparent;
    border: none;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.green};
    border: 2px solid ${({ theme }) => theme.black};
    cursor: pointer;
    pointer-events: auto;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
`;

const DateLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
`;

const DateLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.blueish};
  font-family: "SatoshiRegular";
`;

const parseDateTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const toUtcDayKeyTimestamp = (value) => {
  const timestamp = parseDateTimestamp(value);
  if (timestamp === null) {
    return null;
  }

  const date = new Date(timestamp);
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

const formatDate = (utcDayKeyTimestamp) => {
  const date = new Date(utcDayKeyTimestamp);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const DateRangeSlider = ({ matches = [], selectedMatchIds = [], onRangeChange }) => {
  const datedMatches = useMemo(() => {
    return (matches || []).filter(
      (match) => toUtcDayKeyTimestamp(match?.date) !== null
    );
  }, [matches]);

  const { minDayTimestamp, maxDayTimestamp } = useMemo(() => {
    if (datedMatches.length === 0) {
      return { minDayTimestamp: null, maxDayTimestamp: null };
    }

    const dayTimestamps = datedMatches.map((match) =>
      toUtcDayKeyTimestamp(match.date)
    );

    return {
      minDayTimestamp: Math.min(...dayTimestamps),
      maxDayTimestamp: Math.max(...dayTimestamps),
    };
  }, [datedMatches]);

  const totalDays = useMemo(() => {
    if (minDayTimestamp === null || maxDayTimestamp === null) {
      return 0;
    }
    return Math.floor((maxDayTimestamp - minDayTimestamp) / DAY_MS) + 1;
  }, [maxDayTimestamp, minDayTimestamp]);

  const maxDayIndex = Math.max(0, totalDays - 1);

  const { selectedStartDay, selectedEndDay } = useMemo(() => {
    if (totalDays < 1 || selectedMatchIds.length === 0) {
      return { selectedStartDay: 0, selectedEndDay: maxDayIndex };
    }

    const selectedMatchIdSet = new Set(
      (selectedMatchIds || []).map((selectedMatchId) => String(selectedMatchId))
    );
    const selectedDayTimestamps = datedMatches
      .filter((match) => selectedMatchIdSet.has(String(match.id)))
      .map((match) => toUtcDayKeyTimestamp(match.date));

    if (selectedDayTimestamps.length === 0) {
      return { selectedStartDay: 0, selectedEndDay: maxDayIndex };
    }

    const oldestDay = Math.min(...selectedDayTimestamps);
    const newestDay = Math.max(...selectedDayTimestamps);

    const startDay = Math.max(
      0,
      Math.min(maxDayIndex, Math.floor((oldestDay - minDayTimestamp) / DAY_MS))
    );
    const endDay = Math.max(
      startDay,
      Math.min(maxDayIndex, Math.floor((newestDay - minDayTimestamp) / DAY_MS))
    );

    return { selectedStartDay: startDay, selectedEndDay: endDay };
  }, [datedMatches, maxDayIndex, minDayTimestamp, selectedMatchIds, totalDays]);

  const startDayRef = useRef(0);
  const endDayRef = useRef(maxDayIndex);
  const draggingRef = useRef(false);

  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const activeTrackRef = useRef(null);
  const startLabelRef = useRef(null);
  const endLabelRef = useRef(null);

  const getPercentForDay = useCallback(
    (day) => {
      if (maxDayIndex === 0) {
        return 0;
      }
      return (day / maxDayIndex) * 100;
    },
    [maxDayIndex]
  );

  const syncDom = useCallback(
    (startDay, endDay) => {
      if (!Number.isFinite(minDayTimestamp)) {
        return;
      }

      if (startInputRef.current) {
        startInputRef.current.value = String(startDay);
      }
      if (endInputRef.current) {
        endInputRef.current.value = String(endDay);
      }

      if (activeTrackRef.current) {
        const left = getPercentForDay(startDay);
        const right = 100 - getPercentForDay(endDay);
        activeTrackRef.current.style.left = `${left}%`;
        activeTrackRef.current.style.right = `${right}%`;
      }

      if (startLabelRef.current) {
        startLabelRef.current.textContent = formatDate(
          minDayTimestamp + startDay * DAY_MS
        );
      }

      if (endLabelRef.current) {
        endLabelRef.current.textContent = formatDate(minDayTimestamp + endDay * DAY_MS);
      }
    },
    [getPercentForDay, minDayTimestamp]
  );

  useEffect(() => {
    if (draggingRef.current) {
      return;
    }
    startDayRef.current = selectedStartDay;
    endDayRef.current = selectedEndDay;
    syncDom(selectedStartDay, selectedEndDay);
  }, [selectedEndDay, selectedStartDay, syncDom]);

  const handleStartInput = useCallback(
    (nativeEvent) => {
      draggingRef.current = true;
      const rawValue = Number(nativeEvent.target.value);
      const nextStartDay = Math.min(rawValue, endDayRef.current);
      startDayRef.current = nextStartDay;
      nativeEvent.target.value = String(nextStartDay);
      syncDom(nextStartDay, endDayRef.current);
    },
    [syncDom]
  );

  const handleEndInput = useCallback(
    (nativeEvent) => {
      draggingRef.current = true;
      const rawValue = Number(nativeEvent.target.value);
      const nextEndDay = Math.max(rawValue, startDayRef.current);
      endDayRef.current = nextEndDay;
      nativeEvent.target.value = String(nextEndDay);
      syncDom(startDayRef.current, nextEndDay);
    },
    [syncDom]
  );

  const commitRangeChange = useCallback(() => {
    draggingRef.current = false;

    if (
      !onRangeChange ||
      !Number.isFinite(minDayTimestamp) ||
      !Number.isFinite(startDayRef.current) ||
      !Number.isFinite(endDayRef.current)
    ) {
      return;
    }

    const startDate = new Date(minDayTimestamp + startDayRef.current * DAY_MS);
    const endDate = new Date(
      minDayTimestamp + endDayRef.current * DAY_MS + DAY_MS - 1
    );

    onRangeChange(startDate, endDate);
  }, [minDayTimestamp, onRangeChange]);

  if (datedMatches.length < 2 || totalDays < 2 || !Number.isFinite(minDayTimestamp)) {
    return null;
  }

  const initialLeft = getPercentForDay(selectedStartDay);
  const initialRight = 100 - getPercentForDay(selectedEndDay);

  return (
    <SliderContainer>
      <SliderLabel>Date Range</SliderLabel>
      <RangeWrapper>
        <Track />
        <ActiveTrack
          ref={activeTrackRef}
          style={{ left: `${initialLeft}%`, right: `${initialRight}%` }}
        />
        <RangeInput
          ref={startInputRef}
          type="range"
          min={0}
          max={maxDayIndex}
          defaultValue={selectedStartDay}
          onInput={handleStartInput}
          onMouseUp={commitRangeChange}
          onTouchEnd={commitRangeChange}
        />
        <RangeInput
          ref={endInputRef}
          type="range"
          min={0}
          max={maxDayIndex}
          defaultValue={selectedEndDay}
          onInput={handleEndInput}
          onMouseUp={commitRangeChange}
          onTouchEnd={commitRangeChange}
        />
      </RangeWrapper>
      <DateLabels>
        <DateLabel ref={startLabelRef}>
          {formatDate(minDayTimestamp + selectedStartDay * DAY_MS)}
        </DateLabel>
        <DateLabel ref={endLabelRef}>
          {formatDate(minDayTimestamp + selectedEndDay * DAY_MS)}
        </DateLabel>
      </DateLabels>
    </SliderContainer>
  );
};

export default DateRangeSlider;
